import React, { useState, useEffect, useRef } from 'react';
import './SciFiLogin.css';

const MAX_LENGTH = 6;
const CORRECT_PASS = '566500';

const SciFiLogin = ({ onLoginSuccess }) => {
    const [passcode, setPasscode] = useState(Array(MAX_LENGTH).fill(''));
    const [status, setStatus] = useState('idle'); // idle, checking, denied, granted
    const [terminalText, setTerminalText] = useState('');
    const [clientInfo, setClientInfo] = useState({ ip: 'DETECTING...', location: 'CALCULATING COORDINATES...' });
    const inputRefs = useRef([]);

    useEffect(() => {
        // IP Fetch
        fetch('https://ipapi.co/json/')
            .then(res => res.json())
            .then(data => {
                if (data.ip) {
                    setClientInfo({ ip: data.ip, location: `${data.city}, ${data.country_name}` });
                } else {
                    setClientInfo({ ip: 'NETWORK_SECURE', location: 'UNDISCLOSED' });
                }
            })
            .catch(() => setClientInfo({ ip: 'SECURE_NODE_9', location: 'UNDISCLOSED' }));

        // Typing effect for the intro terminal text
        const text = "AWAITING AUTHORIZATION_CODE...";
        let i = 0;
        const typingInterval = setInterval(() => {
            setTerminalText(text.substring(0, i));
            i++;
            if (i > text.length) clearInterval(typingInterval);
        }, 50);

        // Auto-focus first input slightly after render
        setTimeout(() => {
            if (inputRefs.current[0]) inputRefs.current[0].focus();
        }, 500);

        return () => clearInterval(typingInterval);
    }, []);

    const handleChange = (e, index) => {
        if (status !== 'idle') return;
        
        const val = e.target.value;
        
        if (val === '') {
            const newPasscode = [...passcode];
            newPasscode[index] = '';
            setPasscode(newPasscode);
            return;
        }

        // Only take the last character typed
        const char = val.slice(-1);
        
        // Only allow numbers
        if (!/^[0-9]*$/.test(char)) return;
        
        const newPasscode = [...passcode];
        newPasscode[index] = char;
        setPasscode(newPasscode);

        // Move to next input automatically
        if (char !== '' && index < MAX_LENGTH - 1) {
            inputRefs.current[index + 1].focus();
        }

        // Check if full
        if (newPasscode.every(p => p !== '')) {
            verifyPasscode(newPasscode.join(''));
        }
    };

    const handleKeyDown = (e, index) => {
        if (status !== 'idle') return;

        // Move to previous on backspace if empty
        if (e.key === 'Backspace' && passcode[index] === '' && index > 0) {
            inputRefs.current[index - 1].focus();
        }
        
    };

    const handleClick = (index) => {
        if (inputRefs.current[index]) {
            inputRefs.current[index].setSelectionRange(1, 1);
        }
    };

    const verifyPasscode = (code) => {
        setStatus('checking');
        
        // Small delay for cinematic effect
        setTimeout(() => {
            if (code === CORRECT_PASS) {
                setStatus('granted');
                // Wait for flash animation then login
                setTimeout(() => {
                    onLoginSuccess();
                }, 1500);
            } else {
                setStatus('denied');
                // Shake and flash red, then reset
                setTimeout(() => {
                    setPasscode(Array(MAX_LENGTH).fill(''));
                    setStatus('idle');
                    if (inputRefs.current[0]) inputRefs.current[0].focus();
                }, 1000);
            }
        }, 800);
    };

    return (
        <div className="scifi-login-container">
            {status === 'denied' && <div className="glitch-overlay" />}
            {status === 'granted' && <div className="unlock-overlay" />}

            <div className="scifi-logo-box">
                <img src="/icon.svg" alt="BöcekSoft Icon" style={{ height: '150px', marginBottom: '15px' }} />
                <img src="/logo-text.svg" alt="BöcekSoft Text" style={{ height: '105px' }} />
                <div className="scifi-title" style={{ marginTop: '20px' }}>SECURE TERMINAL</div>
            </div>

            <div className="scifi-terminal-text typing-cursor">
                {terminalText}
            </div>

            <div className={`passcode-container ${status === 'denied' ? 'access-denied' : ''} ${status === 'granted' ? 'access-granted' : ''}`}>
                {passcode.map((char, i) => (
                    <input
                        key={i}
                        name={`scifi_node_${i}`}
                        type="tel"
                        inputMode="numeric"
                        maxLength={2}
                        ref={el => inputRefs.current[i] = el}
                        value={char ? '*' : ''}
                        onChange={(e) => handleChange(e, i)}
                        onKeyDown={(e) => handleKeyDown(e, i)}
                        onClick={() => handleClick(i)}
                        className={`sci-fi-input ${char !== '' ? 'filled' : ''} ${status === 'checking' ? 'checking-anim' : ''}`}
                        readOnly={status !== 'idle'}
                        autoComplete="off"
                        spellCheck="false"
                        data-form-type="other"
                    />
                ))}
            </div>

            <div className="status-message">
                {status === 'denied' && <span className="status-denied">ACCESS DENIED</span>}
                {status === 'granted' && <span className="status-granted">ACCESS GRANTED. DECRYPTING...</span>}
            </div>

            <div className="client-footer">
                <div className="client-trace-title">[ CONNECTION TRACE ]</div>
                <div>IP: <span className="client-data">{clientInfo.ip}</span></div>
                <div>LOC: <span className="client-data">{clientInfo.location}</span></div>
            </div>
        </div>
    );
};

export default SciFiLogin;
